import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Calendar, DollarSign, CheckCircle, AlertCircle, Shield, Lock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to safely format dates
const safeFormatDate = (dateString, defaultText = 'N/A') => {
  if (!dateString) return defaultText;
  try {
    const date = new Date(dateString);
    // Check for invalid date or epoch date (1969/1970)
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
      return defaultText;
    }
    return date.toLocaleDateString();
  } catch {
    return defaultText;
  }
};

// Helper to check if date is valid
const isValidDate = (dateString) => {
  if (!dateString) return false;
  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime()) && date.getFullYear() >= 2000;
  } catch {
    return false;
  }
};

const MembershipRenewal = ({ member, membershipPlan, onRenewSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [renewalPreview, setRenewalPreview] = useState(null);

  useEffect(() => {
    if (member && member.membership_plan_id) {
      fetchRenewalPreview();
    }
  }, [member, membershipPlan]);

  const fetchRenewalPreview = async () => {
    try {
      const today = new Date();
      let daysUntilExpiry = 0;
      let newEndDate;
      
      // Check if membership_end_date is valid
      const hasValidEndDate = isValidDate(member.membership_end_date);
      
      if (hasValidEndDate) {
        const endDate = new Date(member.membership_end_date);
        daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
        
        // Calculate new end date (extends from current end date if not expired)
        if (endDate < today) {
          // Expired - start from today
          newEndDate = new Date(today);
        } else {
          // Not expired - extend from current end
          newEndDate = new Date(endDate);
        }
      } else {
        // No valid end date - start from today
        newEndDate = new Date(today);
        daysUntilExpiry = 0;
      }
      
      newEndDate.setMonth(newEndDate.getMonth() + (membershipPlan?.duration_months || 12));
      
      // Determine if this is a first payment (needs enrollment fee)
      const isFirstTimePayment = !hasValidEndDate || !member.membership_start_date || !member.enrollment_fee_paid;
      
      // Fetch enrollment fee settings if it's a first payment
      let enrollmentFee = 0;
      if (isFirstTimePayment) {
        try {
          const settingsResponse = await axios.get(`${API}/subscriptions/clinic-settings`);
          if (settingsResponse.data.enrollment_fee_enabled) {
            enrollmentFee = settingsResponse.data.enrollment_fee || 0;
          }
        } catch (err) {
          console.error('Error fetching enrollment fee settings:', err);
        }
      }
      
      const planPrice = membershipPlan?.price || 0;
      
      setRenewalPreview({
        currentPlan: membershipPlan?.name || 'Unknown Plan',
        expiryDate: hasValidEndDate ? member.membership_end_date : null,
        daysUntilExpiry,
        renewalPrice: planPrice,
        enrollmentFee: enrollmentFee,
        totalPrice: planPrice + enrollmentFee,
        newExpiryDate: newEndDate.toISOString(),
        hasValidEndDate,
        isFirstTimePayment
      });
    } catch (error) {
      console.error('Failed to fetch renewal preview:', error);
    }
  };

  const handleRenewal = async () => {
    setLoading(true);
    try {
      // Get the current origin URL for redirects
      const originUrl = window.location.origin;
      
      // Call backend to create Stripe renewal checkout session
      const response = await axios.post(`${API}/payments/checkout/renewal-session`, {
        member_id: member.id,
        origin_url: originUrl
      });

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Renewal failed:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
      setLoading(false);
    }
  };

  if (!member || !membershipPlan) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('membership.noActiveMembershipYet')}</p>
        </CardContent>
      </Card>
    );
  }

  const isExpiringSoon = renewalPreview && renewalPreview.hasValidEndDate && renewalPreview.daysUntilExpiry <= 30 && renewalPreview.daysUntilExpiry >= 0;
  const isExpired = renewalPreview && renewalPreview.hasValidEndDate && renewalPreview.daysUntilExpiry < 0;
  const needsDateFix = renewalPreview && !renewalPreview.hasValidEndDate;
  
  // Determine if this is a first-time payment or a renewal
  const isFirstPayment = needsDateFix || !member.membership_start_date || renewalPreview?.isFirstTimePayment;
  
  // Calculate display price (include enrollment fee for first payment)
  const displayPrice = isFirstPayment && renewalPreview?.enrollmentFee > 0 
    ? renewalPreview?.totalPrice 
    : renewalPreview?.renewalPrice;
  
  // Labels based on payment type
  const cardTitle = isFirstPayment ? t('renewal.completeMembership') : t('renewal.membershipRenewal');
  const cardDescription = isFirstPayment 
    ? t('renewal.payToActivate')
    : t('renewal.renewToEnjoy');
  const buttonText = isFirstPayment 
    ? `${t('renewal.payNow')} - $${displayPrice?.toFixed(2)}`
    : `${t('renewal.renewMembership')} - $${renewalPreview?.renewalPrice?.toFixed(2)}`;
  const badgeText = isFirstPayment 
    ? t('renewal.paymentRequired')
    : (isExpired ? t('membership.expired') : t('subscription.membershipExpiring', { count: renewalPreview?.daysUntilExpiry || 0, days: renewalPreview?.daysUntilExpiry || 0 }));

  return (
    <Card className={`border-2 ${isExpiringSoon || needsDateFix || isFirstPayment ? 'border-orange-400' : 'border-gray-200'}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{cardTitle}</CardTitle>
            <CardDescription>{cardDescription}</CardDescription>
          </div>
          {(isExpiringSoon || isExpired || needsDateFix || isFirstPayment) && (
            <Badge className={isFirstPayment ? "bg-blue-500" : "bg-orange-500"}>
              {badgeText}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Membership Info */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('subscriptionManager.currentPlan')}</span>
            <span className="font-semibold text-gray-900">{renewalPreview?.currentPlan}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{isFirstPayment ? t('payments.status') : t('renewal.currentExpiry')}</span>
            <span className="font-semibold text-gray-900">
              {isFirstPayment ? t('membership.pendingPayment') : safeFormatDate(renewalPreview?.expiryDate, t('membership.notSet'))}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t('renewal.membershipStatus')}</span>
            <Badge className={isFirstPayment || isExpired || needsDateFix ? 'bg-orange-500' : 'bg-green-500'}>
              {isFirstPayment ? t('renewal.awaitingPayment') : (needsDateFix ? t('renewal.needsActivation') : member.membership_status)}
            </Badge>
          </div>
        </div>

        {/* Payment Details */}
        <div className="border-t pt-4">
          <h4 className="font-semibold text-gray-900 mb-4">{isFirstPayment ? t('renewal.paymentDetails') : t('renewal.renewalDetails')}</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-teal-600" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">{t('renewal.membershipPrice')}</p>
                <p className="text-xl font-bold text-gray-900">
                  ${renewalPreview?.renewalPrice?.toFixed(2)}
                </p>
              </div>
            </div>
            
            {/* Enrollment Fee - only show for first payment */}
            {isFirstPayment && renewalPreview?.enrollmentFee > 0 && (
              <div className="flex items-center gap-3 bg-blue-50 p-3 rounded-lg">
                <Shield className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm text-blue-700">{t('renewal.enrollmentFee')}</p>
                  <p className="text-lg font-bold text-blue-900">
                    ${renewalPreview?.enrollmentFee?.toFixed(2)}
                  </p>
                </div>
              </div>
            )}
            
            {/* Total - show if there's an enrollment fee */}
            {isFirstPayment && renewalPreview?.enrollmentFee > 0 && (
              <div className="flex items-center gap-3 bg-teal-50 p-3 rounded-lg border-2 border-teal-200">
                <DollarSign className="h-5 w-5 text-teal-700" />
                <div className="flex-1">
                  <p className="text-sm text-teal-700 font-medium">{t('renewal.totalFirstPayment')}</p>
                  <p className="text-2xl font-bold text-teal-900">
                    ${renewalPreview?.totalPrice?.toFixed(2)}
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-teal-600" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">{isFirstPayment ? t('renewal.validUntil') : t('renewal.newExpiryDate')}</p>
                <p className="font-semibold text-gray-900">
                  {safeFormatDate(renewalPreview?.newExpiryDate)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-teal-600" />
              <div className="flex-1">
                <p className="text-sm text-gray-600">{t('membership.duration')}</p>
                <p className="font-semibold text-gray-900">
                  {membershipPlan?.duration_months} {t('renewal.months')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Secure Payment Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800">
              {t('renewal.securePayment')}
            </p>
          </div>
        </div>

        {/* Payment Button */}
        <Button
          onClick={handleRenewal}
          disabled={loading}
          className={`w-full ${isFirstPayment 
            ? 'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700' 
            : 'bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700'}`}
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              {t('renewal.redirecting')}
            </>
          ) : (
            <>
              <Lock className="h-4 w-4 mr-2" />
              {buttonText}
            </>
          )}
        </Button>

        <p className="text-xs text-gray-500 text-center">
          {isFirstPayment 
            ? t('renewal.activatedAfterPayment')
            : t('renewal.extendedFromExpiry')
          }
        </p>
      </CardContent>
    </Card>
  );
};

export default MembershipRenewal;
